import { useState, useRef } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AGENTS, PIPELINE_STEPS } from "@/lib/agents";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Rocket, Check, Loader2, Sparkles } from "lucide-react";
import PackageView from "@/components/agency/PackageView";

const agentById = (id) => AGENTS.find((a) => a.id === id);

export default function PipelineRunner({ brand, onDone }) {
  const [running, setRunning] = useState(false);
  const [active, setActive] = useState(-1);
  const [done, setDone] = useState([]);
  const [pkg, setPkg] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ objetivo: "", formato: "", tema: "" });
  const timers = useRef([]);

  const run = async () => {
    if (!brand) { toast.error("Selecione uma marca primeiro."); return; }
    setRunning(true); setActive(0); setDone([]); setPkg(null); setOpen(true);

    // animate pipeline steps while the API works
    timers.current.forEach(clearTimeout);
    timers.current = [];
    PIPELINE_STEPS.forEach((_, i) => {
      const t = setTimeout(() => {
        setActive(i);
        setDone((d) => (i > 0 ? [...new Set([...d, i - 1])] : d));
      }, i * 1100);
      timers.current.push(t);
    });

    try {
      const res = await api.post(`/brands/${brand.brand_id}/generate`, form);
      timers.current.forEach(clearTimeout);
      setDone(PIPELINE_STEPS.map((_, i) => i));
      setActive(PIPELINE_STEPS.length);
      setPkg(res.data);
      toast.success("Pacote de conteúdo entregue pela agência!");
      onDone?.();
    } catch (e) {
      timers.current.forEach(clearTimeout);
      toast.error(e.response?.data?.detail || "Erro ao gerar o conteúdo.");
      setOpen(false);
    } finally {
      setRunning(false);
    }
  };

  return (
    <div>
      <div className="glass rounded-2xl p-6">
        <p className="overline text-emerald-400 mb-1">Comando principal</p>
        <h2 className="font-display text-2xl font-bold tracking-tight">Criar conteúdo para minha marca</h2>
        <p className="text-sm text-slate-400 mt-1">
          O Orquestrador aciona os 7 agentes em sequência e entrega um pacote completo e adaptado por plataforma
          {brand ? <> para <span className="text-emerald-300 font-medium">{brand.nome}</span>.</> : "."}
        </p>

        <div className="grid sm:grid-cols-3 gap-4 mt-5">
          <div>
            <Label className="text-slate-300 text-sm mb-1.5 block">Objetivo (opcional)</Label>
            <Input data-testid="input-pipeline-objetivo" placeholder="Ex: Gerar leads" value={form.objetivo}
              onChange={(e) => setForm({ ...form, objetivo: e.target.value })}
              className="bg-slate-900/60 border-slate-700 focus-visible:ring-emerald-500/40" />
          </div>
          <div>
            <Label className="text-slate-300 text-sm mb-1.5 block">Formato (opcional)</Label>
            <Input data-testid="input-pipeline-formato" placeholder="Ex: Reel, Carrossel" value={form.formato}
              onChange={(e) => setForm({ ...form, formato: e.target.value })}
              className="bg-slate-900/60 border-slate-700 focus-visible:ring-emerald-500/40" />
          </div>
          <div>
            <Label className="text-slate-300 text-sm mb-1.5 block">Tema (opcional)</Label>
            <Input data-testid="input-pipeline-tema" placeholder="Ex: Dicas de treino" value={form.tema}
              onChange={(e) => setForm({ ...form, tema: e.target.value })}
              className="bg-slate-900/60 border-slate-700 focus-visible:ring-emerald-500/40" />
          </div>
        </div>

        <Button data-testid="btn-criar-conteudo-pipeline" onClick={run} disabled={running || !brand} size="lg"
          className="mt-5 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold hover:brightness-110 transition-all group">
          {running ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Rocket className="w-5 h-5 mr-2 group-hover:-translate-y-0.5 transition-transform" />}
          Criar conteúdo para minha marca
        </Button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-slate-950 border-slate-800 text-slate-100 max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-emerald-400" />
              {pkg ? `Pacote final — ${brand?.nome}` : "A agência está trabalhando…"}
            </DialogTitle>
          </DialogHeader>

          {!pkg && (
            <div className="space-y-2 py-2">
              {PIPELINE_STEPS.map((s, i) => {
                const ag = agentById(s.agent);
                const isActive = active === i;
                const isDone = done.includes(i);
                const Icon = ag.icon;
                return (
                  <motion.div key={s.key} data-testid={`pipeline-step-${s.key}`}
                    initial={{ opacity: 0.4 }} animate={{ opacity: isActive || isDone ? 1 : 0.45 }}
                    className={`flex items-center gap-3 rounded-xl border p-3 ${isActive ? "border-emerald-500/40 bg-emerald-950/20" : "border-slate-800 bg-slate-900/40"}`}>
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${isActive ? "pulse-node" : ""}`}
                      style={{ backgroundColor: ag.color + "22", border: `1px solid ${ag.color}55` }}>
                      {isDone ? <Check className="w-4 h-4 text-emerald-400" /> : <Icon className="w-4 h-4" style={{ color: ag.color }} />}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium">{s.label}</p>
                      <p className="font-mono text-xs text-slate-500">{ag.nome}</p>
                    </div>
                    {isActive && <Loader2 className="w-4 h-4 animate-spin text-emerald-400" />}
                    {isDone && <span className="text-xs text-emerald-400 font-mono">ok</span>}
                  </motion.div>
                );
              })}
            </div>
          )}

          {pkg && <PackageView pkg={pkg} brand={brand} onScheduled={onDone} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
